import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearCart, removeFromCart, updateQuantity } from "../../store";
import { submitQuote } from "../utils/shopApi";
import { NAV } from "../Navbar/navbarTheme";
import { AUTH_ACCENT, AUTH_PRIMARY } from "./authStyles";

const PRIMARY = AUTH_PRIMARY;
const ACCENT = AUTH_ACCENT;
const LINE = NAV.line;
const PAGE_BG = "#FFFFFF";
const NOTE_MAX = 400;

const itemCardSx = {
  borderRadius: 3,
  border: `1px solid ${LINE}`,
  boxShadow: "0 12px 40px rgba(15, 40, 71, 0.08)",
  bgcolor: NAV.surface,
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 16px 48px rgba(15, 40, 71, 0.12)",
  },
};

const qtyButtonSx = {
  borderRadius: 0,
  width: 36,
  height: 36,
  color: PRIMARY,
  "&:hover": { bgcolor: NAV.navyMuted },
};

function parsePrice(value) {
  if (value == null || value === "") return 0;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function formatGBP(amount) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function parseWeight(value) {
  if (value == null || value === "") return null;
  const n = parseFloat(String(value).replace(/[^0-9.-]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}

function formatWeight(kg) {
  if (kg == null) return "—";
  return `${new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(kg)} kg`;
}

function disp(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

const detailRow = (label, value) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="flex-start"
    gap={1}
    sx={{ py: 0.35 }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: 600,
        color: PRIMARY,
        textAlign: "right",
        wordBreak: "break-word",
      }}
    >
      {disp(value)}
    </Typography>
  </Stack>
);

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const items = useSelector((state) => state.cart.items);
  const authToken = useSelector((state) => state.auth.token);
  const isAuthenticated = Boolean(authToken);
  const lineCount = items.reduce((sum, entry) => sum + entry.quantity, 0);
  const location = useLocation();
  const [note, setNote] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const totalGrossWeight = useMemo(() => {
    let total = 0;
    let hasWeight = false;
    for (const item of items) {
      const unitGrossWeight = parseWeight(item.gross_weight);
      if (unitGrossWeight != null) {
        total += unitGrossWeight * (item.quantity ?? 1);
        hasWeight = true;
      }
    }
    return hasWeight ? total : null;
  }, [items]);

  const handleSubmitQuote = async () => {
    setSubmitError("");

    if (!isAuthenticated) {
      navigate("/SignIn", { state: { from: "/Cart" } });
      return;
    }

    const lines = items
      .filter((item) => item?.id != null && Number(item?.quantity) > 0)
      .map((item) => ({
        product_id: Number(item.id),
        quantity: Number(item.quantity),
      }));

    if (lines.length === 0) {
      setSubmitError(
        "Please add at least one product before requesting a quote.",
      );
      return;
    }

    try {
      setIsSubmitting(true);
      await submitQuote({
        note: note.trim(),
        lines,
      });
      setNote("");
      dispatch(clearCart());
      setSuccessModalOpen(true);
    } catch (error) {
      if (error?.code === "AUTH_REQUIRED") {
        navigate("/SignIn", { state: { from: "/Cart" } });
        return;
      }
      setSubmitError(
        error?.message || "Failed to submit quote request. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        pt: "clamp(104px, 14vh, 150px)",
        pb: { xs: 4, md: 6 },
        px: { xs: 2, sm: 3 },
        maxWidth: 1320,
        mx: "auto",
        minHeight: "70vh",
        background: PAGE_BG,
      }}
    >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #163a5e 55%, #1a4570 100%)`,
          boxShadow: "0 18px 48px rgba(15, 40, 71, 0.18)",
          color: "#fff",
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: { xs: 52, sm: 60 },
              height: { xs: 52, sm: 60 },
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "rgba(255,255,255,0.14)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.25)",
              flexShrink: 0,
            }}
          >
            <ShoppingCartOutlinedIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.02em",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                lineHeight: 1.15,
              }}
            >
              Basket
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontSize: "0.9375rem",
                opacity: 0.9,
                maxWidth: 420,
                lineHeight: 1.5,
              }}
            >
              Review your lines and submit a tailored quote request.
            </Typography>
          </Box>
        </Stack>
        {items.length > 0 ? (
          <Chip
            label={`${lineCount} ${lineCount === 1 ? "unit" : "units"} · ${items.length} ${items.length === 1 ? "line" : "lines"}`}
            sx={{
              fontWeight: 700,
              bgcolor: "rgba(255,255,255,0.16)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.28)",
              backdropFilter: "blur(6px)",
              height: 36,
              "& .MuiChip-label": { px: 1.5 },
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" },
          gap: { xs: 2, md: 2.5 },
          alignItems: "flex-start",
        }}
      >
        <Stack spacing={2}>
          {items.length === 0 ? (
            <Card elevation={0} sx={itemCardSx}>
              <CardContent
                sx={{
                  py: { xs: 5, md: 6 },
                  px: { xs: 2.5, md: 4 },
                  background:
                    "radial-gradient(circle at 18% 22%, rgba(15, 40, 71, 0.06) 0, transparent 48%), radial-gradient(circle at 84% 10%, rgba(227, 24, 55, 0.08) 0, transparent 42%)",
                }}
              >
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 84,
                      height: 84,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: NAV.navyMuted,
                      color: PRIMARY,
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
                    }}
                  >
                    <ShoppingCartOutlinedIcon sx={{ fontSize: 44 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      color: PRIMARY,
                      letterSpacing: "0.01em",
                    }}
                  >
                    Your basket is empty
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 400, lineHeight: 1.7 }}
                  >
                    Browse products and add them to your basket — pricing and
                    case details will appear here.
                  </Typography>
                  <Button
                    component={Link}
                    to="/products"
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: 999,
                      bgcolor: ACCENT,
                      boxShadow: "0 12px 28px rgba(227, 24, 55, 0.28)",
                      px: 3.5,
                      py: 1.1,
                      "&:hover": {
                        bgcolor: "#c8102e",
                        boxShadow: "0 14px 34px rgba(227, 24, 55, 0.34)",
                      },
                    }}
                  >
                    Continue shopping
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            items.map((item) => {
              const unitPrice = parsePrice(item.price);
              const hasPrice =
                item.price != null && String(item.price).trim() !== "";
              const unitGrossWeight = parseWeight(item.gross_weight);
              const lineGrossWeight =
                unitGrossWeight != null
                  ? unitGrossWeight * (item.quantity ?? 1)
                  : null;
              const hasGrossWeight = unitGrossWeight != null;

              return (
                <Card key={item.id} elevation={0} sx={itemCardSx}>
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", sm: "flex-start" }}
                    >
                      <Box
                        sx={{
                          p: 1.25,
                          borderRadius: 2.5,
                          border: `1px solid ${LINE}`,
                          bgcolor: NAV.surfaceSubtle,
                          flexShrink: 0,
                          alignSelf: { xs: "center", sm: "flex-start" },
                        }}
                      >
                        <Box
                          component="img"
                          src={item.image_url || item.image || ""}
                          alt={item.name || ""}
                          sx={{
                            width: { xs: 140, sm: 108 },
                            height: { xs: 140, sm: 108 },
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          gap={1}
                          sx={{ mb: 1.25 }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: { xs: "1rem", sm: "1.0625rem" },
                              color: PRIMARY,
                              lineHeight: 1.35,
                            }}
                          >
                            {item.name}
                          </Typography>
                          {hasPrice && (
                            <Chip
                              label={formatGBP(unitPrice)}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                bgcolor: NAV.navyMuted,
                                color: PRIMARY,
                                flexShrink: 0,
                                borderRadius: 1.5,
                              }}
                            />
                          )}
                        </Stack>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: { xs: 0.25, sm: 0.75 },
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: NAV.chipRowBg,
                            border: `1px solid ${LINE}`,
                          }}
                        >
                          {detailRow("Product Code", item.code)}
                          {/* {detailRow(
                            "Price",
                            hasPrice ? formatGBP(unitPrice) : "—",
                          )} */}
                          {detailRow("Case size", item.case_size)}
                          {/* {detailRow("Layer", item.layer)} */}
                          {detailRow("Gross Weight", item.gross_weight)} kg
                        </Box>

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1.5}
                          sx={{
                            pt: 1.5,
                            borderTop: `1px solid ${LINE}`,
                          }}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                border: `1px solid ${LINE}`,
                                borderRadius: 999,
                                bgcolor: NAV.surfaceSubtle,
                                overflow: "hidden",
                              }}
                            >
                              <IconButton
                                size="small"
                                aria-label="Decrease quantity"
                                onClick={() =>
                                  dispatch(
                                    updateQuantity({
                                      itemId: item.id,
                                      change: -1,
                                    }),
                                  )
                                }
                                sx={qtyButtonSx}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <Typography
                                sx={{
                                  fontWeight: 800,
                                  minWidth: 32,
                                  textAlign: "center",
                                  color: PRIMARY,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {item.quantity}
                              </Typography>
                              <IconButton
                                size="small"
                                aria-label="Increase quantity"
                                onClick={() =>
                                  dispatch(
                                    updateQuantity({
                                      itemId: item.id,
                                      change: 1,
                                    }),
                                  )
                                }
                                sx={qtyButtonSx}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            <IconButton
                              size="small"
                              aria-label="Remove item"
                              onClick={() => dispatch(removeFromCart(item.id))}
                              sx={{
                                color: NAV.inkMuted,
                                border: `1px solid ${LINE}`,
                                borderRadius: 999,
                                "&:hover": {
                                  color: ACCENT,
                                  bgcolor: "rgba(227, 24, 55, 0.06)",
                                  borderColor: "rgba(227, 24, 55, 0.25)",
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 2,
                              bgcolor: NAV.navyMuted,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: PRIMARY,
                                fontSize: "1rem",
                              }}
                            >
                              Total weight:{" "}
                              {hasGrossWeight
                                ? formatWeight(lineGrossWeight)
                                : "—"}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Stack>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${LINE}`,
            boxShadow: "0 16px 48px rgba(15, 40, 71, 0.1)",
            bgcolor: NAV.surface,
            overflow: "hidden",
            height: "fit-content",
            position: { lg: "sticky" },
            top: { lg: 120 },
          }}
        >
          <Box
            sx={{
              px: 2.5,
              py: 2,
              background: `linear-gradient(135deg, ${PRIMARY} 0%, #163a5e 100%)`,
              color: "#fff",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: "0.02em" }}
            >
              Order summary
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", opacity: 0.88, mt: 0.35 }}>
              Quote request overview
            </Typography>
          </Box>
          <CardContent sx={{ p: 2.5 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Line items</Typography>
                <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                  {items.length}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Total Case</Typography>
                <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                  {lineCount}
                </Typography>
              </Stack>
              {/* <Stack direction="row" justifyContent="space-between">
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography fontWeight={700} sx={{ color: PRIMARY }}>
                  {formatWeight(totalGrossWeight)}
                </Typography>
              </Stack> */}

              <Divider sx={{ my: 0.5 }} />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  py: 1,
                  px: 1.25,
                  borderRadius: 2,
                  bgcolor: NAV.chipRowBg,
                  border: `1px solid ${LINE}`,
                }}
              >
                <Typography sx={{ fontWeight: 800, color: PRIMARY }}>
                  Estimated total weight
                </Typography>
                <Typography
                  sx={{ fontWeight: 800, color: PRIMARY, fontSize: "1.2rem" }}
                >
                  {formatWeight(totalGrossWeight)}
                </Typography>
              </Stack>
            </Stack>

            <Box sx={{ mt: 2.25 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: 0.75 }}
              >
                <Typography sx={{ fontWeight: 700, color: PRIMARY }}>
                  Note
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color:
                      note.length > NOTE_MAX ? "error.main" : "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {note.length}/{NOTE_MAX}
                </Typography>
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Please quote MOQ."
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
                inputProps={{ maxLength: NOTE_MAX }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: NAV.surfaceSubtle,
                    "& fieldset": { borderColor: LINE },
                    "&:hover fieldset": { borderColor: "#c5d0dc" },
                    "&.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>

            {!isAuthenticated && items.length > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                Sign in to your trade account to submit this quote request.
              </Alert>
            )}

            {submitError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            <Button
              fullWidth
              disabled={items.length === 0 || isSubmitting}
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleSubmitQuote}
              sx={{
                mt: 2.5,
                py: 1.4,
                textTransform: "none",
                fontWeight: 800,
                fontSize: "1rem",
                borderRadius: 2,
                bgcolor: ACCENT,
                boxShadow: "0 10px 28px rgba(227, 24, 55, 0.28)",
                "&:hover": { bgcolor: "#c8102e" },
                "&.Mui-disabled": { bgcolor: "#e5e7eb", color: "#9ca3af" },
              }}
            >
              {isSubmitting
                ? "Submitting..."
                : isAuthenticated
                  ? "Request quote"
                  : "Sign in to request quote"}
            </Button>
            {items.length > 0 && (
              <Button
                component={Link}
                to="/products"
                fullWidth
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  color: PRIMARY,
                  borderRadius: 2,
                  py: 1,
                  "&:hover": { bgcolor: NAV.navyMuted },
                }}
              >
                Continue shopping
              </Button>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="quote-success-title"
        aria-describedby="quote-success-desc"
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(15, 40, 71, 0.45)",
              backdropFilter: "blur(8px)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 28px 90px rgba(15, 40, 71, 0.28)",
            border: "1px solid rgba(255,255,255,0.2)",
          },
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(145deg, ${PRIMARY} 0%, #2a3f72 42%, #3d5490 100%)`,
            pt: 4,
            pb: 3,
            px: 3,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Box
            sx={{
              width: 88,
              height: 88,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.08) 100%)",
              boxShadow:
                "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.35)",
            }}
          >
            <TaskAltRoundedIcon sx={{ fontSize: 48, color: "#fff" }} />
          </Box>
          <Typography
            id="quote-success-title"
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "0.02em", mb: 1 }}
          >
            Quote request sent
          </Typography>
          <Typography
            id="quote-success-desc"
            sx={{
              fontSize: "0.95rem",
              opacity: 0.92,
              lineHeight: 1.55,
              maxWidth: 360,
              mx: "auto",
            }}
          >
            Thank you. Our sales team will review your basket and get back to
            you shortly.
          </Typography>
        </Box>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2.5,
            bgcolor: "#fafbfc",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setSuccessModalOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderColor: PRIMARY,
              color: PRIMARY,
              borderRadius: 2,
              py: 1.1,
              "&:hover": {
                borderColor: PRIMARY,
                bgcolor: "rgba(29, 36, 69, 0.06)",
              },
            }}
          >
            Close
          </Button>
          <Button
            fullWidth
            variant="contained"
            component={Link}
            to="/products"
            onClick={() => setSuccessModalOpen(false)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              bgcolor: PRIMARY,
              borderRadius: 2,
              py: 1.1,
              boxShadow: "0 8px 24px rgba(29, 36, 69, 0.3)",
              "&:hover": { bgcolor: "#141a33" },
            }}
          >
            Continue shopping
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CartPage;
